import { Controller } from "@hotwired/stimulus";

type FormatName =
  | "date"
  | "date_short"
  | "time"
  | "time_ms"
  | "time_short"
  | "datetime"
  | "datetime_compact"
  | "datetime_ms"
  | "iso_datetime";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function pad(n: number, width = 2): string {
  return n.toString().padStart(width, "0");
}

function tzAbbreviation(date: Date): string {
  const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(date);
  const tz = parts.find((p) => p.type === "timeZoneName");
  return tz?.value ?? "";
}

function format(date: Date, name: FormatName): string {
  const y = date.getFullYear();
  const mo = MONTHS_SHORT[date.getMonth()];
  const moNum = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const h = pad(date.getHours());
  const mi = pad(date.getMinutes());
  const s = pad(date.getSeconds());
  const ms = pad(date.getMilliseconds(), 3);

  switch (name) {
    case "date":
      return `${mo} ${d}, ${y}`;
    case "date_short":
      return `${y}-${moNum}-${d}`;
    case "time":
      return `${h}:${mi}:${s}`;
    case "time_ms":
      return `${h}:${mi}:${s}.${ms}`;
    case "time_short":
      return `${h}:${mi}`;
    case "datetime":
      return `${mo} ${d}, ${y} ${h}:${mi}:${s}`;
    case "datetime_compact":
      return `${mo} ${d} ${h}:${mi}`;
    case "datetime_ms":
      return `${mo} ${d}, ${y} ${h}:${mi}:${s}.${ms}`;
    case "iso_datetime":
      return `${y}-${moNum}-${d} ${h}:${mi}:${s}`;
    default:
      return date.toString();
  }
}

const FORMATS_WITHOUT_CLOCK: FormatName[] = ["date", "date_short"];

/**
 * Renders a server-emitted <time> element in the browser's local timezone.
 *
 * The server renders the value in UTC with a "UTC" suffix as the fallback.
 * On connect, this controller replaces the inner text with the same value
 * formatted in the local timezone, suffixed with the short TZ name (e.g. "CEST").
 * Date-only formats omit the TZ suffix since timezone is not meaningful.
 *
 * The original UTC string is preserved in the element's `title` attribute so
 * users can hover to see the canonical UTC time.
 */
export default class LocalTimeController extends Controller<HTMLTimeElement> {
  static values = {
    format: { type: String, default: "datetime" },
  };

  declare formatValue: string;

  connect(): void {
    const iso = this.element.getAttribute("datetime");
    if (!iso) return;

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return;

    const formatName = this.formatValue as FormatName;
    const formatted = format(date, formatName);
    if (FORMATS_WITHOUT_CLOCK.includes(formatName)) {
      this.element.textContent = formatted;
    } else {
      const tz = tzAbbreviation(date);
      this.element.textContent = tz ? `${formatted} ${tz}` : formatted;
    }
  }
}
